import React, { useMemo, useState } from 'react';
import { Button, Text, useToast, HStack, VStack } from '@chakra-ui/react';
import { FaDownload, FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { getData } from '../../../utils/httpClient';
import AdminPageShell from '../../../components/layout/AdminPageShell';
import { primaryButtonProps, ghostButtonProps } from '../../../theme/adminUi';
import ReportPeriodBar from './ReportPeriodBar';
import ReportPreviewPanel from './ReportPreviewPanel';
import {
  defaultMonthRange,
  validatePeriod,
  formatMinutes,
  formatDateUy,
} from './reportUtils';

const USER_COLUMNS = [
  { key: 'userName', label: 'Operario' },
  { key: 'asMechanicMinutes', label: 'Como mecánico', isNumeric: true },
  { key: 'servicesAsMechanic', label: 'Servicios', isNumeric: true },
  { key: 'asAssistantMinutes', label: 'Como ayudante', isNumeric: true },
  { key: 'servicesAsAssistant', label: 'Servicios', isNumeric: true },
  { key: 'totalMinutes', label: 'Total', isNumeric: true },
];

const TOGETHER_COLUMNS = [
  { key: 'date', label: 'Fecha' },
  { key: 'vehiclePlate', label: 'Matrícula' },
  { key: 'mechanicName', label: 'Mecánico' },
  { key: 'assistantName', label: 'Ayudante' },
  { key: 'mechanicMinutes', label: 'Mecánico', isNumeric: true },
  { key: 'assistantMinutes', label: 'Ayudante', isNumeric: true },
  { key: 'togetherMinutes', label: 'Juntos', isNumeric: true },
];

export default function ProductivityReport() {
  const toast = useToast();
  const navigate = useNavigate();
  const defaults = useMemo(() => defaultMonthRange(), []);
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [byUser, setByUser] = useState([]);
  const [together, setTogether] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasPreviewed, setHasPreviewed] = useState(false);

  const load = async () => {
    const error = validatePeriod(startDate, endDate);
    if (error) {
      toast({ title: 'Período inválido', description: error, status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    setLoading(true);
    try {
      const res = await getData(
        `/reports/productivity?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
      );
      setByUser(res?.payload?.byUser || []);
      setTogether(res?.payload?.together || []);
      setHasPreviewed(true);
    } catch (err) {
      toast({
        title: 'Error al cargar productividad',
        description: err?.response?.data?.message || err?.message || 'Intentá nuevamente.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      setByUser([]);
      setTogether([]);
      setHasPreviewed(true);
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = () => {
    if (!byUser.length && !together.length) {
      toast({
        title: 'Sin datos',
        description: 'Generá la vista previa antes de descargar.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        byUser.map((u) => ({
          Operario: u.userName,
          'Minutos como mecánico': u.asMechanicMinutes,
          'Servicios como mecánico': u.servicesAsMechanic,
          'Minutos como ayudante': u.asAssistantMinutes,
          'Servicios como ayudante': u.servicesAsAssistant,
          'Total minutos': u.totalMinutes,
        })),
      ),
      'Por operario',
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        together.map((t) => ({
          Fecha: t.date || '',
          Matrícula: t.vehiclePlate || '',
          Mecánico: t.mechanicName || '',
          Ayudante: t.assistantName || '',
          'Min. mecánico': t.mechanicMinutes,
          'Min. ayudante': t.assistantMinutes,
          'Min. juntos': t.togetherMinutes,
        })),
      ),
      'Trabajo conjunto',
    );
    XLSX.writeFile(wb, `productividad_${startDate}_${endDate}.xlsx`);
  };

  return (
    <AdminPageShell
      title="Productividad"
      actions={
        <HStack spacing={2}>
          <Button
            {...ghostButtonProps}
            leftIcon={<FaArrowLeft />}
            onClick={() => navigate('/admin/reports')}
          >
            Volver
          </Button>
          <Button
            {...primaryButtonProps}
            leftIcon={<FaDownload />}
            onClick={downloadExcel}
            isDisabled={!byUser.length && !together.length}
          >
            Descargar Excel
          </Button>
        </HStack>
      }
    >
      <ReportPeriodBar
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onApply={load}
        loading={loading}
        description="Minutos productivos del reloj de mecánico y ayudante. El tiempo juntos usa el solapamiento de sus ventanas de trabajo."
      />

      <VStack spacing={4} align="stretch">
        <ReportPreviewPanel
          title="Por operario"
          count={byUser.length}
          countLabel={`${byUser.length} operarios`}
          loading={loading}
          emptyMessage={
            hasPreviewed
              ? 'Sin datos de productividad en este período.'
              : 'Seleccioná un período y generá la vista previa.'
          }
          columns={USER_COLUMNS}
          rows={byUser}
          getRowKey={(row) => row.userId}
          maxH="40vh"
          renderCell={(row, col) => {
            if (col.key === 'userName') {
              return <Text fontWeight="semibold">{row.userName}</Text>;
            }
            if (col.key.includes('Minutes')) {
              return (
                <Text fontWeight={col.key === 'totalMinutes' ? 'bold' : 'normal'}>
                  {formatMinutes(row[col.key])}
                </Text>
              );
            }
            return row[col.key] ?? 0;
          }}
        />

        <ReportPreviewPanel
          title="Trabajo conjunto"
          count={together.length}
          countLabel={`${together.length} servicios en dupla`}
          loading={false}
          emptyMessage={
            hasPreviewed
              ? 'No hay servicios con mecánico y ayudante en el período.'
              : 'La vista de duplas aparece al generar la preview.'
          }
          columns={TOGETHER_COLUMNS}
          rows={hasPreviewed && !loading ? together : []}
          getRowKey={(row) => row.serviceId}
          maxH="40vh"
          renderCell={(row, col) => {
            if (col.key === 'date') return formatDateUy(row.date);
            if (col.key.includes('Minutes')) {
              return (
                <Text fontWeight={col.key === 'togetherMinutes' ? 'bold' : 'normal'}>
                  {formatMinutes(row[col.key])}
                </Text>
              );
            }
            return row[col.key] || '—';
          }}
        />
      </VStack>
    </AdminPageShell>
  );
}
